import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { IFeedPostItem } from "@eagerdog/interfaces";
import moment from "moment";

import Button from "src/components/Button/Button";
import Icon from "src/components/Icon/Icon";

import styles from "./DogActivityCard.module.scss";

interface IProps {
    post: IFeedPostItem;
    link: string;
}

/** "3 years old" / "7 months old"; nothing when the birthday is unknown or in the future. */
const ageLabel = (dob?: Date | string): string | undefined => {
    if (!dob) return undefined;
    const birth = moment(dob);
    if (!birth.isValid() || birth.isAfter()) return undefined;
    const years = moment().diff(birth, "years");
    if (years >= 1) return `${years} year${years === 1 ? "" : "s"} old`;
    const months = moment().diff(birth, "months");
    return months >= 1 ? `${months} month${months === 1 ? "" : "s"} old` : undefined;
};

// Dog summary inside a dog_followed post, styled like the dog profile's
// identity band: portrait, kicker, call name, registered name, breed / sex /
// age, owner's town, and a "View profile" action. Live details come from the
// feed read while the dog is public; the post's snapshot is the fallback.
const DogActivityCard: React.FC<IProps> = ({ post, link }) => {
    const navigate = useNavigate();
    // On the permalink page the card is subject context for a photo / run /
    // title post: `post.dog` is hydrated but there is no follow activity.
    const a: Partial<NonNullable<IFeedPostItem["activity"]>> = post.activity || {};
    const dog = post.dog;
    if (!post.activity && !dog) return null;

    const name = dog?.call_name || a.target_name || "Dog";
    const image = dog?.image_url || a.target_image_url;
    const registered = dog?.registered_name
        ? [dog.prefix_title, dog.registered_name, dog.suffix_title].filter(Boolean).join(" ")
        : undefined;
    const breed = dog ? [dog.breed, dog.variety].filter(Boolean).join(" - ") : a.target_breed;
    const facts = [breed, dog?.sex, ageLabel(dog?.dob)].filter(Boolean).join(" · ");
    const place = [dog?.city, dog?.state, dog?.country].filter(Boolean).join(", ");

    return (
        <div className={styles.container}>
            <div className={styles.DogActivityCard}>
                <Link to={link} className={styles.portrait} aria-label={name}>
                    {image ? <img src={image} alt={name} /> : <span>{name.charAt(0).toUpperCase()}</span>}
                </Link>
                <div className={styles.text}>
                    <span className={styles.kicker}>Dog profile</span>
                    <Link to={link} className={styles.name}>{name}</Link>
                    {registered && <span className={styles.registered}>{registered}</span>}
                    {facts && <span className={styles.facts}>{facts}</span>}
                    {place && (
                        <span className={styles.place}>
                            <Icon type="location" className={styles.placeIcon} />
                            {place}
                        </span>
                    )}
                </div>
                <Button variant="secondary" className={styles.viewButton} onClick={() => navigate(link)}>
                    View profile
                    <Icon type="caretRight" className={styles.viewCaret} />
                </Button>
            </div>
        </div>
    );
};

export default DogActivityCard;
